import isEmpty from 'lodash/isEmpty';
import { memo, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ErrorLoad, Tabs } from '@wg/wows-react-uikit';
import { TABS_SIZES, TABS_THEMES } from '@wg/wows-react-uikit/components/Tabs/Tabs';

import { root } from '~/preloaded';
import settings from '~/settings';
import { CLAN_WARS_TABS } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import { closeAllDialogs, showCWarsLastRoundDialog } from '~/helpers/dialogs';
import { t } from '~/helpers/localization';
import useMount from '~/hooks/useMount';
import { ROUTES_MAP } from '~/router/routes';
import { useAppDispatch, useAppSelector } from '~/store';
import { currentAccountClanIdSelector, currentAccountSelector } from '~/store/selectors/currentAccountSelector';
import { initClanWarsThunk } from '~/store/thunks/clanWarsThunks';

import { AnimatedFetchingComponent, COMPETITION_METRIC } from '~/UIKit';

import styles from './ClanWars.scss';

import type { Tab } from '@wg/wows-react-uikit/components/Tabs/Tabs';
import type { RootState } from '~/store';
import type { CompetitionMetric } from '~/types/declaration';

export const getReachedGradeTooltipBody = (competitionMetric: CompetitionMetric) => {
  switch (competitionMetric) {
    case COMPETITION_METRIC.EXP: {
      return t('Количество чистого опыта, обеспечивающее увеличение результата клана');
    }
    case COMPETITION_METRIC.DAMAGE: {
      return t('Количество урона, обеспечивающее увеличение результата клана');
    }
    case COMPETITION_METRIC.RIBBONS: {
      return t('Количество лент, обеспечивающее увеличение результата клана');
    }
    default: {
      return '';
    }
  }
};

export const getBattleMeticText = (competitionMetric: CompetitionMetric) => {
  switch (competitionMetric) {
    case COMPETITION_METRIC.EXP: {
      return t('Опыт боя');
    }
    case COMPETITION_METRIC.DAMAGE: {
      return t('Урон за бой');
    }
    case COMPETITION_METRIC.RIBBONS: {
      return t('Лент за бой');
    }
    default: {
      return '';
    }
  }
};

export const getBattleMeticTooltipBody = (competitionMetric: CompetitionMetric) => {
  switch (competitionMetric) {
    case COMPETITION_METRIC.EXP: {
      return t('Чистый опыт полученный в бою, при попытке взять планку');
    }
    case COMPETITION_METRIC.DAMAGE: {
      return t('Количество урона, нанесенного в бою, при попытке взять планку');
    }
    case COMPETITION_METRIC.RIBBONS: {
      return t('Количество лент, полученных во время боя, при попытке взять планку');
    }
    default: {
      return '';
    }
  }
};

const getCurrentTab = (pathname: string) => {
  if (
    pathname.startsWith(`${root}${ROUTES_MAP.CLAN_WARS_WELCOME}`) ||
    pathname.startsWith(`${root}${ROUTES_MAP.CLAN_WARS_WAR}`)
  ) {
    return CLAN_WARS_TABS.WAR;
  }
  if (pathname.startsWith(`${root}${ROUTES_MAP.CLAN_WARS_RATING}`)) {
    return CLAN_WARS_TABS.RATING;
  }
  if (pathname.startsWith(`${root}${ROUTES_MAP.CLAN_WARS_RULES}`)) {
    return CLAN_WARS_TABS.RULES;
  }

  return CLAN_WARS_TABS.WAR;
};

const stateSelector = (state: RootState) => {
  return {
    lastRoundResults: state.ReducerClanWars.lastRoundResults,
    lastRoundResultsHidden: state.ReducerClanWars.lastRoundResultsHidden,
    hasErrors: state.ReducerClanWars.hasErrors,
    isReady: state.ReducerClanWars.isReady,
    isFetching: state.ReducerClanWars.isFetching,
    isFromPort: state.ReducerClanWars.isFromPort,
  };
};

const ClanWars = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { lastRoundResults, lastRoundResultsHidden, hasErrors, isFetching, isReady, isFromPort } = useAppSelector(
    stateSelector,
    shallowEqual,
  );
  const currentAccount = useAppSelector(currentAccountSelector);
  const clanId = useAppSelector(currentAccountClanIdSelector);

  useMount(() => {
    if (!currentAccount.id) {
      return;
    }

    dwhExport.push(DWH_EVENTS.NAVAL_BATTLES.OPEN_PAGE);

    initClanWars(false);

    return () => {
      handleCloseAllDialogs();
    };
  });

  const [currentTab, setCurrentTab] = useState(getCurrentTab(location.pathname));
  useEffect(() => {
    setCurrentTab(getCurrentTab(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (!isEmpty(lastRoundResults) && !lastRoundResultsHidden) {
      dispatch(showCWarsLastRoundDialog());
    }
  }, [dispatch, lastRoundResults, lastRoundResultsHidden]);

  const handleCloseAllDialogs = () => {
    dispatch(closeAllDialogs());
  };
  const initClanWars = (isReload: boolean) => {
    dispatch(initClanWarsThunk(isReload));
  };

  const onToggleTab = (value: CLAN_WARS_TABS) => {
    navigate(`${root}${ROUTES_MAP.CLAN_WARS}/${value}`, { replace: true });
  };

  const reload = () => {
    initClanWars(true);
  };

  const tabs = useMemo(() => {
    const tabs: Tab<CLAN_WARS_TABS>[] = [{ value: CLAN_WARS_TABS.WAR, content: t('Морское сражение') }];

    if (currentAccount.id) {
      tabs.push({ value: CLAN_WARS_TABS.RATING, content: t('Рейтинг кланов') });
    }

    if (settings.cwars.isRulesShown) {
      tabs.push({ value: CLAN_WARS_TABS.RULES, content: t('Правила') });
    }

    return tabs;
  }, [currentAccount.id]);

  if (hasErrors) {
    return (
      <ErrorLoad
        isFlat
        key="error-load"
        message={t('Произошла ошибка. Повторите попытку позже')}
        onReloadClick={reload}
      />
    );
  }

  return (
    <AnimatedFetchingComponent className={styles.wrapper} isFetching={isFetching || (!isReady && !!clanId)}>
      <div className={`${styles.blurableTabs} ${isFromPort ? styles.isBluredTabs : ''}`}>
        <Tabs
          classNameWrapper={styles.tabs}
          theme={TABS_THEMES.CLIENT}
          size={TABS_SIZES.M}
          isUppercase
          smallHeight
          tabSelected={currentTab}
          tabs={tabs}
          onToggleTab={onToggleTab}
        />
      </div>
      <Outlet />
    </AnimatedFetchingComponent>
  );
};

export default memo(ClanWars);
